import VueGoogleMaps from '@fawmi/vue-google-maps';
import { VueSvgIconPlugin } from '@yzfe/vue3-svgicon';
import FloatingVue, { hideAllPoppers as originalHideAllPoppers } from 'floating-vue';
import { inject } from 'inversify';
import { debounce } from 'lodash';
import { vMaska } from 'maska/vue';
import { Userpilot } from 'userpilot';
import { App, Plugin } from 'vue';
// eslint-disable-next-line import/no-extraneous-dependencies
import vueClickOutsideElement from 'vue-click-outside-element';
import { RouteLocation } from 'vue-router';

import { app } from '@/app';
import { SIGNALR, ZOLL_ONLINE, USER_PILOT } from '@/constants';
import { UserController } from '@/controllers/UserController';
import { accessGuard } from '@/controllers/guards/accessGuard';
import { authenticationGuard } from '@/controllers/guards/authenticationGuard';
import { developmentGuard } from '@/controllers/guards/developmentGuard';
import { errorGuard } from '@/controllers/guards/errorGuard';
import { externalGuard } from '@/controllers/guards/externalGuard';
import { helpGuard } from '@/controllers/guards/helpGuard';
import { initializeGuard } from '@/controllers/guards/initializeGuard';
import { logoutGuard } from '@/controllers/guards/logoutGuard';
import { userPilotGuard } from '@/controllers/guards/userPilotGuard';
import { service } from '@/decorators/service';
import { indexedDbFilterManager } from '@/indexedDb/managers/filter';
import { indexedDbLayoutManager } from '@/indexedDb/managers/layout';
import { i18n, onLocaleChange, t } from '@/locales';
import { AlertManager } from '@/managers/AlertManager';
import { AlertTypeManager } from '@/managers/AlertTypeManager';
import { AssignmentWindowManager } from '@/managers/AssignmentWindowManager';
import { AttachmentManager } from '@/managers/AttachmentManager';
import { CallSourceManager } from '@/managers/CallSourceManager';
import { CallTypeManager } from '@/managers/CallTypeManager';
import { CompanyConfigurationManager } from '@/managers/CompanyConfigurationManager';
import { ConfigurationManager } from '@/managers/ConfigurationManager';
import { CrewMemberManager } from '@/managers/CrewMemberManager';
import { EteManager } from '@/managers/EteManager';
import { FacilityManager } from '@/managers/FacilityManager';
import { FilterManager } from '@/managers/FilterManager';
import { GenderManager } from '@/managers/GenderManager';
import { LayoutManager } from '@/managers/LayoutManager';
import { MetaDataManager } from '@/managers/MetaDataManager';
import { NatureManager } from '@/managers/NatureManager';
import { NotificationManager } from '@/managers/NotificationManager';
import { PostManager } from '@/managers/PostManager';
import { ProQAScriptManager } from '@/managers/ProQAScriptManager';
import { RequestCancellationReasonManager } from '@/managers/RequestCancellationReasonManager';
import { ResponseProfileManager } from '@/managers/ResponseProfileManager';
import { ShiftManager } from '@/managers/ShiftManager';
import { SpecialStatusManager } from '@/managers/SpecialStatusManager';
import { TransportRequestManager } from '@/managers/TransportRequestManager';
import { TripManager } from '@/managers/TripManager';
import { UnitManager } from '@/managers/UnitManager';
import { UnitTemplateManager } from '@/managers/UnitTemplateManager';
import { VehicleManager } from '@/managers/VehicleManager';
import { VehiclePositionManager } from '@/managers/VehiclePositionManager';
import { ZoneManager } from '@/managers/ZoneManager';
import { pinia } from '@/pinia';
import { createRoutes } from '@/routes';
import { BetaFeatures } from '@/services/BetaFeatures';
import { Connection } from '@/services/Connection';
import { Environment } from '@/services/Environment';
import { Logger } from '@/services/Logger';
import { Router } from '@/services/router/Router';
import { Signalr } from '@/services/signalr/Signalr';
import { Toaster } from '@/services/toaster/Toaster';
import { TOAST_CONTEXT } from '@/services/toaster/enum';
import type { IToast } from '@/services/toaster/types';
import { VuexStore } from '@/services/vuex/VuexStore';
import { ProxyClass } from '@/utils/ProxyClass';
import { setJwtCookieDomain } from '@/utils/jwt';
import { pathJoin } from '@/utils/pathJoin';
import { toastStatusMsg } from '@/utils/toastStatusMsg';

// Initialize Userpilot
Userpilot.initialize(USER_PILOT.TOKEN); // `appToken` should be replaced with your userpilot appToken.

const hideAllPoppers = debounce(originalHideAllPoppers, 100);

@service(AppController)
export class AppController extends ProxyClass(app) {
  // Services.
  @inject(BetaFeatures) protected betaFeatures: BetaFeatures;
  @inject(Connection) protected connection: Connection;
  @inject(Environment) protected environment: Environment;
  @inject(Logger) protected logger: Logger;
  @inject(Router) protected router: Router;
  @inject(Signalr) protected signalr: Signalr;
  @inject(Toaster) protected toaster: Toaster;
  @inject(ResponseProfileManager) protected responseProfileManager: ResponseProfileManager;
  @inject(UserController) protected userController: UserController;
  @inject(VuexStore) protected vuexStore: VuexStore;

  // Ensure managers register necessary SignalR message handlers prior to SignalR starting.
  // @todo Figure out a better way to handle this.
  @inject(PostManager) protected postManager: PostManager;
  @inject(TransportRequestManager) protected transportRequestManager: TransportRequestManager;
  @inject(LayoutManager) protected layoutManager: LayoutManager;
  @inject(RequestCancellationReasonManager)
  protected requestCancellationReasonManager: RequestCancellationReasonManager;

  @inject(ProQAScriptManager) protected proQaScriptManager: ProQAScriptManager;
  @inject(AlertTypeManager) protected alertTypeManager: AlertTypeManager;
  @inject(AlertManager) protected alertManager: AlertManager;
  @inject(ZoneManager) protected zoneManager: ZoneManager;
  @inject(NotificationManager) protected notificationManager: NotificationManager;
  @inject(AttachmentManager) protected attachmentManager: AttachmentManager;
  @inject(SpecialStatusManager) protected specialStatusManager: SpecialStatusManager;
  @inject(FilterManager) protected filterManager: FilterManager;
  @inject(MetaDataManager) protected metaDataManager: MetaDataManager;
  @inject(CallSourceManager) protected callSourceManager: CallSourceManager;
  @inject(CallTypeManager) protected callTypeManager: CallTypeManager;
  @inject(NatureManager) protected natureManager: NatureManager;
  @inject(FacilityManager) protected facilityManager: FacilityManager;
  @inject(ConfigurationManager) protected configurationManager: ConfigurationManager;
  @inject(GenderManager) protected genderManager: GenderManager;
  @inject(CompanyConfigurationManager) protected companyConfigurationManager: CompanyConfigurationManager;
  @inject(VehicleManager) protected vehicleManager: VehicleManager;
  @inject(UnitTemplateManager) protected unitTemplateManager: UnitTemplateManager;
  @inject(CrewMemberManager) protected crewMemberManager: CrewMemberManager;
  @inject(TripManager) protected tripManager: TripManager;
  @inject(EteManager) protected eteManager: EteManager;
  @inject(VehiclePositionManager) protected vehiclePositionManager: VehiclePositionManager;
  @inject(UnitManager) protected unitManager: UnitManager;
  @inject(ShiftManager) protected shiftManager: ShiftManager;
  // Injected purely to instantiate the manager so its @SignalrMessage listener is
  // registered — the BE pushes AssignmentWindowConfigRespMsg on connect without a request.
  @inject(AssignmentWindowManager) protected assignmentWindowManager: AssignmentWindowManager;

  // Methods.
  async initialize() {
    this.use(pinia);
    // To ensure stores are registered prior to SignalR, each must be defined upon import.
    const stores = [
      await import('@/stores/map'),
      await import('@/stores/ProQaStore'),
    ];

    // Initialize Pinia stores.
    // @todo Remove this once they're converted to decorated store services.
    stores.forEach(({ useStore }) => useStore());

    // Register the Vuex store with the app.
    this.use(this.vuexStore);

    // Set the global Toaster contexts (i18n translations).
    this.toaster.setContexts({
      [TOAST_CONTEXT.ERROR]: () => t('toast.context.error'),
      [TOAST_CONTEXT.INFO]: () => t('toast.context.info'),
      [TOAST_CONTEXT.CONNECTION]: () => t('toast.context.connection'),
      [TOAST_CONTEXT.SUCCESS]: () => t('toast.context.success'),
      [TOAST_CONTEXT.WARNING]: () => t('toast.context.warning'),
    });

    // Start logging Vue specific warnings and errors to LogRocket.
    // @see https://v3.vuejs.org/api/application-config.html#errorhandler
    this.logger.utilities.logWarnError(this as App);

    // Set the cookie domain.
    setJwtCookieDomain(this.environment.zoCookieDomain);

    // Refresh toasts when the locale has changed.
    onLocaleChange(() => this.toaster.refresh());

    // Redirect to login if user is not authorized (logged in).
    if (!this.connection.success) {
      window.location.replace(pathJoin(this.environment.zoBaseUrl, ZOLL_ONLINE.LOGIN_PATH));
      return;
    }

    // Show toast notifications when the SignalR connection state has changed.
    let reconnectingToast: Readonly<IToast> | undefined;
    let reconnectCount = 0;
    let lastDisconnectMs = 0;
    const sessionStartMs = performance.now();

    this.signalr.on('reconnecting', () => {
      reconnectCount += 1;
      lastDisconnectMs = performance.now();

      this.logger.track('signalr.reconnecting', {
        reconnectCount,
        sessionAgeMs: Math.round(performance.now() - sessionStartMs),
        previousRetryCount: this.signalr.lastRetryContext?.previousRetryCount,
        elapsedMilliseconds: this.signalr.lastRetryContext?.elapsedMilliseconds,
      });

      // Don't show another toast if one already exists.
      if (reconnectingToast) {
        return;
      }
      reconnectingToast = this.toaster.connection(() => t('toast.network.reconnecting'));
    });
    this.signalr.on('reconnected', () => {
      const downTimeMs = lastDisconnectMs > 0 ? Math.round(performance.now() - lastDisconnectMs) : 0;

      this.logger.track('signalr.reconnected', {
        reconnectCount,
        downTimeMs,
        sessionAgeMs: Math.round(performance.now() - sessionStartMs),
      });

      this.toaster.dismiss(reconnectingToast);
      reconnectingToast = undefined;
      this.toaster.success(() => t('toast.network.reconnected'));
    });
    this.signalr.on('close', () => {
      this.logger.track('signalr.close', {
        reconnectCount,
        sessionAgeMs: Math.round(performance.now() - sessionStartMs),
      });

      this.toaster.dismiss(reconnectingToast);
      reconnectingToast = undefined;
      this.toaster.connection(() => t('toast.network.disconnected'));
    });
    this.signalr.on('fatal', () => {
      this.logger.track('signalr.fatal', {
        reconnectCount,
        sessionAgeMs: Math.round(performance.now() - sessionStartMs),
      });

      this.toaster.dismiss(reconnectingToast);
      reconnectingToast = undefined;
      this.toaster.error(() => t('toast.error.general'));
    });
    this.signalr.on('error', (e) => {
      this.toaster.error(e);
    });
    this.signalr.on('sessionExpired', () => {
      if (!this.connection.redirect.startsWith('/error')) {
        window.location.replace(this.environment.zoLoginUrl);
      }
    });

    // Proxy status messages to the toaster.
    this.signalr.on('StatusMsg', toastStatusMsg);

    // Register beta features.
    this.betaFeatures.add({
      localeSwitcher: 'Locale Switcher',
      autoScheduler: 'Auto Scheduler',
    });

    // Use plugins.
    this.use(i18n);

    this.use(VueSvgIconPlugin, {
      tagName: 'icon',
    });

    this.use(<Plugin>VueGoogleMaps, {
      load: {
        key: this.connection.googleApiKey,
        libraries: 'places',
        loading: 'async',
      },
    });

    this.use(FloatingVue, {
      distance: 10,
      themes: {
        tooltip: {
          html: true,
        },
        error: {
          $extend: 'tooltip',
          $resetCss: true,
        },
        success: {
          $extend: 'tooltip',
          $resetCss: true,
        },
        info: {
          $extend: 'tooltip',
          $resetCss: true,
        },
        warning: {
          $extend: 'tooltip',
          $resetCss: true,
        },
      },
    });

    this.use(vueClickOutsideElement);

    this.directive('maska', vMaska);

    // Ensure poppers are hidden when user resizes.
    window.addEventListener('resize', hideAllPoppers, { capture: true, passive: true });

    // Determine the base URL for the SignalR connection based on CAD type.
    const signalrBaseUrl = this.connection.isHybrid
      ? this.environment.hybridSignalrBaseUrl
      : this.environment.signalrBaseUrl;

    // Initialize the SignalR connection.
    const session = await this.signalr.start(`${signalrBaseUrl}/${SIGNALR.WEBSOCKET_ENDPOINT}`);

    // Set the active user to the current session.
    this.userController.activeUser = session;

    // Load all the layouts from indexDB
    const companyId = this.connection.isPortal
      ? this.connection.networkedTenant.tenantKey
      : this.userController.activeUser.companyId;
    const allLayoutSettings = await indexedDbLayoutManager.getAllLayouts(
      companyId,
      this.userController.activeUser.userKey,
    );

    if (allLayoutSettings.length > 0) {
      this.layoutManager.updateLayouts(allLayoutSettings);
    }

    // Load all the filters from indexDB
    const allFilterSettings = await indexedDbFilterManager.getAllFilters(
      companyId,
      this.userController.activeUser.userKey,
    );

    if (allFilterSettings.length > 0) {
      this.filterManager.updateFilters(allFilterSettings);
    }

    if (this.connection.isStandalone) {
      // Load zones
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      this.zoneManager.load();
    }

    await this.companyConfigurationManager.load();

    // Add router guards; order is important!
    this.router.addGuards(
      helpGuard,
      externalGuard,
      logoutGuard,
      authenticationGuard,
      userPilotGuard,
      errorGuard,
      developmentGuard,
      initializeGuard,
      accessGuard,
    );

    // Add routes.
    this.router.addRoutes(...createRoutes(this.environment, this.connection));

    this.use(this.router);

    // Handle redirect from connection (i.e. session invalidated).
    let { redirect } = this.connection;

    // Invalid connection; redirect to login.
    if (!this.connection.success && !redirect && this.environment.zoLoginUrl) {
      redirect = this.environment.zoLoginUrl;
    }

    if (redirect) {
      const redirectUrl = new URL(redirect, window.location.origin);
      // Internal redirect.
      if (redirectUrl.origin === window.location.origin) {
        // Only redirect internally if there is a dedicated route and the CAD has been initialized.
        if (this.router.hasPath(redirectUrl.pathname) && this.connection.isInitialized) {
          const route = <RouteLocation>{ path: redirectUrl.pathname };
          if (redirectUrl.searchParams) {
            route.query = Object.fromEntries(new URLSearchParams(redirectUrl.searchParams).entries());
          }
          await this.router.push(route);
        }
      }
      // External redirect.
      else {
        window.location.replace(redirect);
      }
    }

    // Wait for the router to be ready.
    await this.router.isReady();

    this.mount('#app');
  }
}
